import { Accordion } from "@webmotors/eleanor-ui";
import { type ReactNode, useEffect, useState } from "react";
import { useAccordionScroll } from "./use-accordion-scroll.hook";
import type { PreFooterLinksCategory } from "./pre-footer-links.types";
import styles from "./pre-footer-links.module.css";

export type PreFooterLinksProps = {
  /** Optional header node rendered above the accordion (e.g. an H2 or a SectionHeader) */
  header?: ReactNode;
  categories: PreFooterLinksCategory[];
};

export function PreFooterLinks({
  header,
  categories,
}: Readonly<PreFooterLinksProps>) {
  const containerRef = useAccordionScroll();

  // ---------------------------------------------------------------------------
  // TODO: remove this workaround block when Eleanor Accordion becomes controlled.
  // The Accordion initialises its internal state from props only once (on mount),
  // so we force a remount via `key` after hydration to collapse the items.
  // SSR + initial render: expanded=true so bots index all links.
  // After mount: key changes → remount with expanded=false for the user.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // TODO (Eleanor controlled): uncomment the block below and delete the workaround
  // above once Accordion respects prop changes and exposes onToggle.
  //
  // const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>(
  //   () => Object.fromEntries(categories.map((_, i) => [i, false]))
  // );
  // const handleToggle = (index: number, expanded: boolean) => {
  //   setExpandedMap((prev) => ({ ...prev, [index]: expanded }));
  // };
  // ---------------------------------------------------------------------------

  if (!categories.length) return null;

  return (
    <section ref={containerRef} className={styles.Container}>
      {header}

      {/* TODO: remove `key` and `expanded: !mounted` when Eleanor Accordion is controlled */}
      <Accordion
        key={mounted ? "interactive" : "seo"}
        titleTag="h3"
        items={categories.map((category, _index) => ({
          title: category.title,
          children: (
            <div className={styles.LinksContainer}>
              {category.links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  data-qa={link.dataQA}
                  target="_self"
                >
                  {link.label}
                </a>
              ))}
            </div>
          ),
          expanded: !mounted,
          // TODO (Eleanor controlled): replace line above with:
          // expanded: expandedMap[index] ?? false,
          // onToggle: (exp: boolean) => handleToggle(index, exp),
          dataTestid: category.dataTestid,
        }))}
      />
    </section>
  );
}
